import {
  Link,
  styled,
  TabProps,
  Tab,
  useMediaQuery,
  Theme,
  ButtonBase,
  Breakpoint,
} from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Content } from '../color';
import {
  Dropdown, DropdownAnchor, DropdownItem, useDropdownControls,
} from './Dropdown';
import Menu from '../icons/Menu';
import { Tabs, TabsProps } from '../tabs';

type TabLink<TComponent extends FunctionComponent = typeof Link> = TabProps<TComponent>;

export interface NavigationTabProps extends TabLink {
  label: string;
  href: string;
}

export interface NavigationProps extends Omit<TabsProps, 'value'> {
  tabs: NavigationTabProps[];
  activeIndex?: number;
  breakpoint?: Breakpoint;
  secondaryVariant?: boolean;
}

export const NavigationTabs = styled(Tabs)(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
}));

const MenuButton = styled(ButtonBase)(({ theme }) => ({
  color: Content.main,
  padding: theme.spacing(1),
  margin: theme.spacing(2),
}));

function Navigation({
  tabs, activeIndex, breakpoint = 'md', secondaryVariant, ...props
}: NavigationProps) {
  const [iActiveIndex, setIActiveIndex] = useState(activeIndex || 0);
  const isBig = useMediaQuery<Theme>((theme) => theme.breakpoints.up(breakpoint));
  const [anchorEl, handleClick, handleClose] = useDropdownControls();

  useEffect(() => {
    if (activeIndex !== undefined) {
      setIActiveIndex(activeIndex);
    }
  }, [activeIndex]);

  return isBig && !secondaryVariant ? (
    <NavigationTabs value={iActiveIndex} {...props}>
      {tabs.map((tab, i) => (
        <Tab
          component={Link}
          key={tab.label}
          {...tab} // NOTE: order matters! this way component can be overridden
          onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
            setIActiveIndex(i);

            if (tab.onClick) {
              tab.onClick(event);
            }
          }}
        />
      ))}
    </NavigationTabs>
  ) : (
    <>
      <MenuButton onClick={handleClick}>
        <Menu />
      </MenuButton>

      <Dropdown
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {tabs.map((tab) => (
          <DropdownItem key={tab.label}>
            <DropdownAnchor href={tab.href}>{tab.label}</DropdownAnchor>
          </DropdownItem>
        ))}
      </Dropdown>
    </>
  );
}

export default Navigation;
